// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/ai-languages.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * The languages the AYNI chatbot speaks (docs/AI_CHATBOT_PLAN.md §1). The
 * session language is chosen in the AI tab; the model answers in it, and the
 * tab's own strings follow it. The rest of the app stays English.
 */
export const AI_CHAT_LANGUAGES = ['en', 'es', 'nl', 'ca'] as const;

export type AiChatLanguage = typeof AI_CHAT_LANGUAGES[number];

/** Each language's name in English (for the model's instructions) and in itself (for the picker). */
export const AI_LANGUAGE_NAMES: Readonly<Record<AiChatLanguage, { english: string; native: string }>> = {
  en: { english: 'English', native: 'English' },
  es: { english: 'Spanish', native: 'Español' },
  nl: { english: 'Dutch', native: 'Nederlands' },
  ca: { english: 'Catalan', native: 'Català' }
};

export function isAiChatLanguage(value: unknown): value is AiChatLanguage {
  return typeof value === 'string' && (AI_CHAT_LANGUAGES as readonly string[]).includes(value);
}

/**
 * The chat language for a requested or browser language tag ("es-ES" gives
 * "es"), or the fallback when it is not one of the four.
 */
export function normalizeAiChatLanguage(value: unknown, fallback: AiChatLanguage = 'en'): AiChatLanguage {
  if (typeof value !== 'string') return fallback;
  const primary = value.trim().toLowerCase().split(/[-_]/)[0];
  return isAiChatLanguage(primary) ? primary : fallback;
}
